/**
 * Chart / task-state helpers for the AskAsper message processor.
 * Split out of messageProcessor.ts; used while folding SSE events into the loading message.
 */

import type { AgenticChatMessage, AgenticTask } from '../types';

type ChartEntry = { viz_data: any; task_id?: string; task_name?: string; scenario_link?: string };

/** Payload with task_id, task_name, run_id (shared by task_narrative, table, viz events) */
export type TaskEventPayload = { task_id: string; task_name: string; run_id?: string };

export function updateTaskLoadingState(
    loadingMessage: AgenticChatMessage,
    json: TaskEventPayload,
    key: 'narrative_loading' | 'table_loading' | 'viz_loading',
    value: boolean,
): AgenticChatMessage {
    const tasks = loadingMessage.additional_kwargs?.tasks ?? [];
    const existingIdx = tasks.findIndex((t) => t.task_id === json.task_id);
    const baseTask: AgenticTask =
        existingIdx >= 0
            ? (tasks[existingIdx] as AgenticTask)
            : {
                  task_id: json.task_id,
                  task_name: json.task_name,
                  status: 'executing',
              };
    const updatedTask = { ...baseTask, [key]: value };
    const updatedTasks =
        existingIdx >= 0
            ? tasks.map((t, i) => (i === existingIdx ? updatedTask : t))
            : [...tasks, updatedTask];
    return {
        ...loadingMessage,
        run_id: json.run_id ?? loadingMessage.run_id,
        additional_kwargs: {
            ...loadingMessage.additional_kwargs,
            tasks: updatedTasks,
        },
    };
}

/** Order charts first (BarChart, etc.), then tables. Applied after any chart update. */
export function chartsOrderedChartsFirstThenTables(charts: ChartEntry[]): ChartEntry[] {
    return [...charts].sort((a, b) => {
        const aIsTable = String(a.viz_data?.chart_type ?? '').toLowerCase() === 'table';
        const bIsTable = String(b.viz_data?.chart_type ?? '').toLowerCase() === 'table';
        if (aIsTable === bIsTable) return 0;
        return aIsTable ? 1 : -1; // non-table first
    });
}

export function replaceLoadingPlaceholder(
    charts: Array<{ viz_data: any; task_id?: string; task_name?: string }>,
    taskId: string,
    chartType: string,
    vizData: any,
    taskName?: string,
): Array<{ viz_data: any; task_id?: string; task_name?: string }> {
    const typeStr = String(chartType ?? '').toLowerCase();
    let replaced = false;
    const result = charts.map((c) => {
        if (replaced) return c;
        const placeholderType = String(c.viz_data?.chart_type ?? '').toLowerCase();
        const isMatch =
            c.task_id === taskId &&
            c.viz_data?._loading === true &&
            (typeStr === 'table' ? placeholderType === 'table' : placeholderType !== 'table');
        if (isMatch) {
            replaced = true;
            return { viz_data: vizData, task_id: taskId, task_name: taskName ?? c.task_name };
        }
        return c;
    });
    if (!replaced) {
        result.push({ viz_data: vizData, task_id: taskId, task_name: taskName });
    }
    return result;
}

/** Replace placeholder with multiple charts (e.g. table_data/chart_data as array). */
export function replaceLoadingPlaceholderWithCharts(
    charts: ChartEntry[],
    taskId: string,
    chartType: string,
    chartItems: Array<{ viz_data: any; scenario_link?: string; task_id?: string; task_name?: string }>,
    taskName?: string,
): ChartEntry[] {
    const typeStr = String(chartType ?? '').toLowerCase();
    const filtered = chartItems.filter((item) => item?.viz_data);
    if (filtered.length === 0) return charts;

    const newCharts = filtered.map((v, vizIdx) => ({
        viz_data: v.viz_data,
        task_id: v.task_id ?? (filtered.length > 1 ? `${taskId}_viz_${vizIdx}` : taskId),
        task_name: v.task_name ?? taskName,
        scenario_link: typeof v.scenario_link === 'string' ? v.scenario_link : undefined,
    }));

    let replaced = false;
    const result = charts.filter((c) => {
        const placeholderType = String(c.viz_data?.chart_type ?? '').toLowerCase();
        const isMatch =
            c.task_id === taskId &&
            c.viz_data?._loading === true &&
            (typeStr === 'table' ? placeholderType === 'table' : placeholderType !== 'table');
        if (isMatch && !replaced) {
            replaced = true;
            return false; // remove placeholder
        }
        return true;
    });
    return [...result, ...newCharts];
}
